// /src/components/data-display/SingleDataValue.jsx
import { memo, useMemo } from 'react';
import { Flex, Text, Link, Badge, Tooltip } from '@chakra-ui/react';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { DATA_STATES } from '../../constants/dataStates';

export const SingleDataValue = memo(({
  id,
  label,
  value,
  mb,
  link,
  display,
  onClick,
  animation,
  hoverColor,
  // New props for error handling
  dataState = DATA_STATES.SUCCESS,
  errorMessage = null,
  isFallback = false,
  showBadges = true
}) => {
  const { colors } = useThemedStyles();
  
  const styles = useMemo(() => ({
    label: {
      color: colors.text.label,
      hover: colors.text.labelHover
    },
    value: {
      color: colors.text.primary,
      hover: colors.text.primary
    }
  }), [colors]);

  const linkedFontSize = link ? '16px' : '20px';
  const LOADING_TEXT = 'Loading...'

  // Determine badge to show based on state
  const getBadge = () => {
    if (!showBadges) return null;

    if (isFallback) {
      return (
        <Tooltip label="Calculated from mempool data or Diamondback API" fontSize="xs">
          <Badge colorScheme="orange" fontSize="9px" variant="solid" ml={1}>
            CALC
          </Badge>
        </Tooltip>
      );
    }

    if (dataState === DATA_STATES.STALE) {
      return (
        <Tooltip label="Data is older than 10 minutes" fontSize="xs">
          <Badge colorScheme="yellow" fontSize="9px" variant="outline" ml={1}>
            STALE
          </Badge>
        </Tooltip>
      );
    }

    if (dataState === DATA_STATES.INVALID) {
      return (
        <Tooltip label="Data validation failed" fontSize="xs">
          <Badge colorScheme="red" fontSize="9px" variant="outline" ml={1}>
            INVALID
          </Badge>
        </Tooltip>
      );
    }

    if (dataState === DATA_STATES.ERROR) {
      return (
        <Tooltip label={errorMessage || 'Failed to load'} fontSize="xs">
          <Badge colorScheme="red" fontSize="9px" variant="solid" ml={1}>
            ERROR
          </Badge>
        </Tooltip>
      );
    }

    return null;
  };

  // Determine display value based on state
  const getDisplayValue = () => {
    // If value is explicitly provided, use it
    if (value != null && value !== '') return value;

    // Otherwise show state-based message
    switch (dataState) {
      case DATA_STATES.LOADING:
        return LOADING_TEXT;
      case DATA_STATES.ERROR:
        return errorMessage || 'Error';
      case DATA_STATES.STALE:
        return value || 'Stale';
      case DATA_STATES.INVALID:
        return 'Invalid';
      default:
        return LOADING_TEXT;
    }
  };

  // Determine value text color based on state
  const getValueColor = () => {
    switch (dataState) {
      case DATA_STATES.ERROR:
        return 'red.500';
      case DATA_STATES.INVALID:
        return 'red.400';
      case DATA_STATES.STALE:
        return 'yellow.600';
      default:
        return styles.value.color;
    }
  };

  const displayValue = getDisplayValue();
  const valueColor = getValueColor();
  const badge = getBadge();

  return (
    <Flex
      id={id}
      direction="column"
      gap="6px"
      minW="0"
      w="100%"
      textAlign="center"
      display={display}
      cursor="pointer"
      onClick={() => onClick(id)}
      animation={animation}
      transformOrigin="center center"
      borderRadius="md"
      p="0"
      overflow="visible"
      _hover={{
        '.label': { color: styles.label.hover },
        '.value': { color: styles.value.hover },
      }}
    >
      <Flex align="center" justify="center" gap={1}>
        <Text
          className="label"
          fontSize={{
            base: "12px",
            xs: "12px",
            sm: "11px",
            sm2: "11px",
            md: "11px",
            lg: "11px",
            xl: "11px",
            '2xl': "12px"
          }}
          color={styles.label.color}
          fontWeight="700"
          lineHeight="1"
          textTransform="uppercase"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
        {badge}
      </Flex>

      <Flex align="center" justify={{ base: "center", md: "center" }}>
        {link ? (
          <Link href={link} isExternal>
            <Flex align="center">
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <Flex ml={2} alignItems="center">
                <Text
                  className="value"
                  fontSize={linkedFontSize}
                  fontWeight="700"
                  lineHeight="1"
                  align={{ base: 'center', md: 'unset' }}
                  mb={mb}
                  mr={2}
                  color={valueColor}
                  whiteSpace="nowrap"
                >
                  {displayValue}
                </Text>
              </Flex>
            </Flex>
          </Link>
        ) : (
          <Text
            className="value"
            fontSize={{
              base: "18px",
              xs: "18px",
              sm: "20px",
              sm2: "22px",
              md: "24px",
              lg: "24px",
              xl: "24px",
              '2xl': "26px"
            }}
            fontWeight="700"
            lineHeight="1"
            mb={mb}
            color={valueColor}
            whiteSpace="nowrap"
          >
            {displayValue}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if key props change
  return (
    prevProps.label === nextProps.label &&
    prevProps.value === nextProps.value &&
    prevProps.link === nextProps.link &&
    prevProps.animation === nextProps.animation &&
    prevProps.dataState === nextProps.dataState &&
    prevProps.errorMessage === nextProps.errorMessage &&
    prevProps.isFallback === nextProps.isFallback
  );
});
